import { getEditorGraphScopeId } from '../editor/editorSession.js';
import { getLayerGraphLibrariesFor } from '../state/layerGraphSelectors.js';
import type { AppState } from '../state/appState.js';
import { renderWebGPU, renderSceneWebGPU } from '../rendering/webgpuRenderer.js';
import type { LayoutResult } from '../scene/layoutEngine.js';
import { renderSceneCanvas } from '../rendering/canvasSceneRenderer.js';
import { createDefaultLayerScene, type Scene } from '../scene/sceneState.js';
import type { SdfTheme } from '../rendering/sdfTheme.js';
import {
  buildSceneContextFor,
  getStandaloneGraphsFor,
  type BuildSceneContextOptions,
  type StandaloneGraphs,
} from './viewportSceneContext.js';
import type { ViewportResolution } from './viewportResolution.js';
import { getRenderThemeValues } from './viewportRenderTheme.js';
import type { DataRuntimeManager } from './dataRuntime.js';

interface ViewportRenderParams {
  appState: AppState;
  canvas: HTMLCanvasElement;
  resolution: ViewportResolution;
  renderSlot: 'geometry' | 'material';
  sdfTheme: SdfTheme;
  dataRuntime?: DataRuntimeManager;
}

interface SceneViewportRenderParams extends ViewportRenderParams {
  scene: Scene;
  sceneContextOptions?: BuildSceneContextOptions;
}

interface StandaloneViewportRenderResult {
  rendered: boolean;
  layoutResult: LayoutResult | null;
}

export async function renderSceneViewportFor(params: SceneViewportRenderParams): Promise<LayoutResult> {
  const { appState, canvas, resolution, scene, renderSlot, sdfTheme, sceneContextOptions, dataRuntime } = params;
  const renderTheme = getRenderThemeValues();
  let { layoutResult, layoutContext } = buildSceneContextFor(appState, resolution, scene, {
    ...sceneContextOptions,
    renderTheme,
  });
  if (dataRuntime) {
    const evaluated = dataRuntime.evaluate(layoutContext, resolution);
    ({ layoutResult, layoutContext } = buildSceneContextFor(appState, resolution, scene, {
      ...sceneContextOptions,
      renderTheme,
      runtimeParams: evaluated.globalParams,
      behaviorParams: evaluated.behaviorParams,
      session: evaluated.session,
    }));
  }

  if (appState.backend === 'webgpu') {
    await renderSceneWebGPU(
      navigator.gpu,
      canvas,
      resolution.w,
      resolution.h,
      scene,
      layoutResult,
      layoutContext,
      appState.layerLibrary,
      getLayerGraphLibrariesFor(appState),
      renderSlot,
      appState.colormap,
      appState.aaMode,
      appState.aaWidth,
      appState.arcCoverageMode,
      sdfTheme,
      renderTheme,
      { ...appState.builtInShapes, ...appState.shapes },
      appState.lights,
    );
    return layoutResult;
  }

  const ctx2d = canvas.getContext('2d');
  if (!ctx2d) return layoutResult;
  renderSceneCanvas(ctx2d, resolution.w, resolution.h, scene, layoutResult, {
    layoutContext,
    renderSlot,
    colormap: appState.colormap,
    aaMode: appState.aaMode,
    aaWidth: appState.aaWidth,
    arcCoverageMode: appState.arcCoverageMode,
    sdfTheme,
  });
  return layoutResult;
}

export async function renderStandaloneViewportFor(
  params: ViewportRenderParams & { standaloneGraphs?: StandaloneGraphs },
): Promise<StandaloneViewportRenderResult> {
  const { appState, canvas, resolution, renderSlot, sdfTheme, standaloneGraphs } = params;
  const preview = standaloneGraphs ?? getStandaloneGraphsFor(appState);
  const renderTheme = getRenderThemeValues();

  if (appState.backend === 'webgpu') {
    if (!preview) return { rendered: false, layoutResult: null };
    const graphScopeId = preview.graphScopeId ?? getEditorGraphScopeId(appState.editorContext) ?? 'layer_main';
    await renderWebGPU(
      navigator.gpu,
      canvas,
      resolution.w,
      resolution.h,
      preview.geometry.nodes,
      preview.geometry.edges,
      preview.material,
      appState.params,
      renderSlot,
      appState.colormap,
      sdfTheme,
      appState.aaMode,
      appState.aaWidth,
      graphScopeId,
      appState.arcCoverageMode,
      renderTheme,
      { ...appState.builtInShapes, ...appState.shapes },
      appState.lights,
    );
    return { rendered: true, layoutResult: null };
  }

  const ctx2d = canvas.getContext('2d');
  if (!ctx2d) return { rendered: true, layoutResult: null };
  const previewScene = createDefaultLayerScene('layer_main');
  const { layoutResult, layoutContext } = buildSceneContextFor(appState, resolution, previewScene, {
    standaloneGraphs: preview ?? undefined,
    renderTheme,
  });
  renderSceneCanvas(ctx2d, resolution.w, resolution.h, previewScene, layoutResult, {
    layoutContext,
    renderSlot,
    colormap: appState.colormap,
    aaMode: appState.aaMode,
    aaWidth: appState.aaWidth,
    arcCoverageMode: appState.arcCoverageMode,
    sdfTheme,
  });
  return { rendered: true, layoutResult };
}
